import isEmpty from 'lodash/isEmpty';
import { arrayOf, bool, func, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';

import BulkToggleControls from '../../../generic/BulkToggleControls';
import withSelection from '../../../generic/Table/with-selection';
import { invoiceShape, productShape } from '../../../prop-types';
import api from '../../../REST/api';
import withResolved from '../../../REST/with-resolved';
import { COLORS } from '../../../utils/constants';
import { flash } from '../../../utils/flash';
import { enrichNestedWithId } from '../../../utils/replace-nested-with-id';
import { allStatusesEnum as invoiceStatuses } from '../../constants';
import { isInvoiceApprovable } from '../../utils';
import RejectButton from '../UpdateInvoice/UpdateInvoiceStatus/RejectButton';
import ApprovableInvoiceLine from './ApprovableInvoiceLine';
import FilterInvoices from './FilterInvoices';

const getDataWithIds = data =>
  data.map(invoice => enrichNestedWithId(invoice, 'owner'));
function ListInReviewInvoices({
  data,
  products,
  onUpdateStatus,
  loading,
  onIsSelected,
  onToggleSelection,
  onToggleSelectionMultiple,
  selectedItems,
}) {
  const dataWithIds = getDataWithIds(data);

  const [filter, setFilter] = useState({
    billingType: undefined,
    type: undefined,
    ownerId: undefined,
    merchantIds: undefined,
  });
  const [invoices, setInvoices] = useState(dataWithIds);

  const handleFilterChange = event => {
    const { name, value } = event.target;
    setFilter({ ...filter, [name]: value });
  };

  // This effect re renders the component when data prop
  // changes from the parent
  useEffect(() => {
    const updatedDataWithIds = getDataWithIds(data);
    setInvoices(updatedDataWithIds);
  }, [data]);

  useEffect(() => {
    const filteredInvoices = Object.entries(filter).reduce(
      (previousInvoicesArray, [filterKey, filterValue]) => {
        return previousInvoicesArray.filter(invoice => {
          const isArray = Array.isArray(invoice[filterKey]);
          if (!filterValue) return true;
          if (isArray) {
            const arrayIds = invoice[filterKey].map(element => element.id);
            return arrayIds.includes(filterValue);
          }
          return filterValue === invoice[filterKey];
        });
      },
      dataWithIds,
    );
    setInvoices(filteredInvoices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleApproveAll = () => {
    selectedItems.forEach(id => {
      const invoice = invoices.find(inv => inv.id === Number(id));
      if (!isInvoiceApprovable(invoice)) {
        flash(
          `INV-${id} cannot be approved since the associated account is missing a business partner id.`,
          COLORS.DANGER,
          0,
        );
      } else {
        onUpdateStatus(Number(id), invoiceStatuses.APPROVED.value);
      }
    });
  };

  const handleRejectAll = message => {
    selectedItems.forEach(id =>
      onUpdateStatus(
        Number(id),
        invoiceStatuses.CHANGES_REQUESTED.value,
        message,
      ),
    );
  };

  const hasNoSelectedItems = !selectedItems.length;

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between">
        <div>
          <BulkToggleControls
            onToggleAll={isSelected => {
              onToggleSelectionMultiple(
                invoices.map(item => `select-${item.id}`),
                isSelected,
              );
            }}
            entities={invoices}
            activeEntities={selectedItems}
          />
        </div>
        <div className="d-flex">
          <RejectButton
            onUpdateStatus={() => {}}
            onSubmit={handleRejectAll}
            id="cancel-selected"
            text="Request changes to selected invoices"
            disabled={hasNoSelectedItems}
          />
          <Button
            color={COLORS.SUCCESS}
            className="ml-2"
            disabled={hasNoSelectedItems}
            onClick={handleApproveAll}
          >
            Approve selected invoices
          </Button>
        </div>
      </div>
      <div className="d-flex justify-content-center mt-4 mb-4">
        <FilterInvoices
          billingType={filter.billingType}
          type={filter.type}
          ownerId={filter.ownerId}
          salesChannel={filter.salesChannel}
          merchantIds={filter.merchantIds}
          onChange={handleFilterChange}
        />
      </div>
      {isEmpty(invoices) ? (
        <div className="p-3">Nothing to review!</div>
      ) : (
        invoices.map(invoice => (
          <ApprovableInvoiceLine
            invoice={invoice}
            products={products}
            key={invoice.id}
            onUpdateStatus={onUpdateStatus}
            loading={loading}
            onIsSelected={onIsSelected}
            onToggleSelection={onToggleSelection}
          />
        ))
      )}
    </div>
  );
}

ListInReviewInvoices.propTypes = {
  data: arrayOf(invoiceShape).isRequired,
  products: arrayOf(productShape.isRequired).isRequired,
  onUpdateStatus: func.isRequired,
  loading: bool.isRequired,
  onToggleSelectionMultiple: func.isRequired,
  onToggleSelection: func.isRequired,
  onIsSelected: func.isRequired,
  selectedItems: arrayOf(string.isRequired).isRequired,
};

export default withResolved({ query: api.getProducts, as: 'products' })(
  withSelection({})(ListInReviewInvoices),
);
